import React, { useMemo } from 'react';
import { useWindow } from '../../contexts/WindowContext';
import { modernDarkTheme, modernLightTheme } from '../../helpers/codeHandler';
import '../../Styles/Layout/Resources/CodeDiffViewer.css';

type DiffLine =
  | { kind: 'equal'; content: string; oldNum: number; newNum: number }
  | { kind: 'removed'; content: string; oldNum: number }
  | { kind: 'added'; content: string; newNum: number };

function computeLineDiff(prev: string, curr: string): DiffLine[] {
  const a = prev.split('\n');
  const b = curr.split('\n');
  const n = a.length;
  const m = b.length;

  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0, j = 0, oldN = 1, newN = 1;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'equal', content: a[i], oldNum: oldN++, newNum: newN++ });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: 'removed', content: a[i], oldNum: oldN++ });
      i++;
    } else {
      out.push({ kind: 'added', content: b[j], newNum: newN++ });
      j++;
    }
  }
  while (i < n) { out.push({ kind: 'removed', content: a[i], oldNum: oldN++ }); i++; }
  while (j < m) { out.push({ kind: 'added', content: b[j], newNum: newN++ }); j++; }
  return out;
}

interface CodeDiffViewerProps {
  previousContent: string;
  currentContent: string;
}

const CodeDiffViewer: React.FC<CodeDiffViewerProps> = ({ previousContent, currentContent }) => {
  const { resolvedTheme } = useWindow();
  const lines = useMemo(
    () => computeLineDiff(previousContent || '', currentContent || ''),
    [previousContent, currentContent],
  );

  const theme = resolvedTheme === 'dark' ? modernDarkTheme : modernLightTheme;
  const bg =
    (theme as any)['pre[class*="language-"]']?.background ||
    (resolvedTheme === 'dark' ? '#181a1b' : '#f5f5f5');

  return (
    <pre className="code-diff-viewer" style={{ background: bg }}>
      {lines.map((ln, idx) => {
        const oldNum = ln.kind === 'added' ? '' : ln.oldNum;
        const newNum = ln.kind === 'removed' ? '' : ln.newNum;
        const marker = ln.kind === 'added' ? '+' : ln.kind === 'removed' ? '-' : ' ';
        return (
          <div key={idx} className={`code-diff-line code-diff-${ln.kind}`}>
            <span className="code-diff-gutter" aria-hidden>{oldNum}</span>
            <span className="code-diff-gutter" aria-hidden>{newNum}</span>
            <span className="code-diff-marker" aria-hidden>{marker}</span>
            <span className="code-diff-content">{ln.content || ' '}</span>
          </div>
        );
      })}
    </pre>
  );
};

export default CodeDiffViewer;
